/** @jsx jsx */

import { Fragment } from 'react';

import { DateTime } from '@zapier/date';
import { Alert, Margin, Spacer, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Link from 'app/common/components/Link';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import ResubmissionSteps from 'app/developer-v3/components/ResubmissionSteps';
import { CardsChecklist } from 'app/developer-v3/components/LaunchProgress/CardsChecklist';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import LaunchProgress, {
  Status,
} from 'app/developer-v3/components/LaunchProgress';
import { useAvroEvent, useQueryPartnerDashboard } from 'app/developer-v3/hooks';

import Congrats from './Congrats';
import { queryOptions } from './constants';
import { Stats, TierGraph } from './Dashboard';

const stylePartnershipLevel = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const styleSubmissionInfo = css`
  margin-bottom: 25px;
`;

const styleTierGraph = css`
  margin-bottom: 1em;
  padding: 1em 0;
`;

type Props = {
  appId: number;
  appName: string;
  isBeta: boolean;
  isPublic: boolean;
  isPublished: boolean;
  serviceId: number;
  slug: string | null;
  status: Status;
};

/**
 * This page is listed as "Dashboard"
 * in the main side navigation menu of dev platform
 */

export default function DashboardPage({
  appId,
  appName,
  isBeta,
  isPublic,
  isPublished,
  serviceId,
  slug,
  status,
}: Props) {
  const { emitPartnerUserInteractionEvent } = useAvroEvent();
  const { data: partnerDashboard, error, isLoading } = useQueryPartnerDashboard(
    { serviceId },
    queryOptions
  );

  const statusDeadline = partnerDashboard?.status?.deadline;
  const hasStatusTier = partnerDashboard?.status?.tier;
  // TODO: Fetch review status on Bouncer, assuming the integration was reviewed
  // on Bouncer..
  // import { ReviewStatus } from 'app/developer-v3/constants/reviews';
  const shouldResubmit = false; // status === ReviewStatus.pushedBack,

  return (
    <ManagerLayout title="Dashboard">
      <Margin margin="0px 0px 15px">
        <Alert
          status="info"
          title="Promote your integration with the Partner Marketing Hub"
        >
          Access co-brandable assets, messaging guidance, and tools to determine
          ROI—all in one place.{' '}
          <Link href="https://partnerhub.zapier.com" target="_blank">
            Explore the Hub
          </Link>
          .
        </Alert>
      </Margin>
      <Margin margin="0px 0px 0px 0px">
        {!isLoading && (
          <Fragment>
            {isPublished && error ? (
              <Fragment>
                <Alert status="error">{error.message}</Alert>
                <Spacer height={20} />
              </Fragment>
            ) : (
              !hasStatusTier && (
                <Fragment>
                  <Alert status="info">
                    Oops! Looks like we don’t have any data for your integration
                    just yet. If you just launched your integration today, your
                    dashboard will populate tomorrow. If not, please contact{' '}
                    <Link href="mailto:partners@zapier.com">
                      partners@zapier.com
                    </Link>
                    .
                  </Alert>
                  <Spacer height={20} />
                </Fragment>
              )
            )}
            {isPublic ? (
              hasStatusTier && (
                <div>
                  <CardsChecklist
                    appId={appId}
                    order="reverse"
                    title="Actions"
                  />
                  <Text tag="h3" type="ParagraphHeader3">
                    Status
                  </Text>
                  <Stats appId={appId} isBeta={false} serviceId={serviceId} />
                  <Spacer height={30} />
                  <div css={stylePartnershipLevel}>
                    <Text tag="h3" type="ParagraphHeader3">
                      Partnership Level
                    </Text>
                    <Link
                      href={`${ZAPIER_APP_BASE_URL}/developer-platform/partner-program`}
                      onClick={() => {
                        emitPartnerUserInteractionEvent({
                          eventAction: 'click',
                          interactionGoal:
                            'Navigate user to the Partner Program benefits page',
                          interactionName: 'explore_your_benefits_link_clicked',
                        });
                      }}
                      target="_blank"
                    >
                      Explore your benefits
                    </Link>
                  </div>
                  <Spacer height={10} />
                  <Text type="Body1">
                    Next evaluation date:{' '}
                    <Text type="Body3">
                      <DateTime date={statusDeadline} />
                    </Text>
                  </Text>
                  <TierGraph
                    css={styleTierGraph}
                    status={partnerDashboard.status}
                  />
                </div>
              )
            ) : (
              <Fragment>
                <div css={styleSubmissionInfo}>
                  {shouldResubmit ? (
                    <ResubmissionSteps integrationId={appId} />
                  ) : (
                    <Congrats appId={appId} slug={slug} status={status} />
                  )}
                </div>
                <LaunchProgress
                  integrationId={appId}
                  integrationName={appName}
                  status={status}
                />
                {isBeta && hasStatusTier && (
                  <Stats appId={appId} isBeta={isBeta} serviceId={serviceId} />
                )}
              </Fragment>
            )}
          </Fragment>
        )}
      </Margin>
    </ManagerLayout>
  );
}
